import eventTrackingErrorsModule from '../../../services/eventTrackingErrors';
const moment = require('moment');

export default {
  data() {
    return {
      fetchingData: true,
      summaryData: null,
      dateRange: null,
      defaultDuration: {
        start: null,
        end: null
      },
      chartOptions: null,

      // Timing filter properties
      timingFilter: 'last-7',
      customTimeRange: null,
      displayTimeRange: null,
      eventTrackingErrorsTimingOptions: [
        {
          label: 'Weekly Durations',
          options: [
            {
              title: 'Last 7 Days',
              value: 'last-7'
            },
            {
              title: 'Current Week',
              value: 'this-week'
            },
            {
              value: 'last-week',
              title: 'Last Week'
            }
          ]
        },
        {
          label: 'Monthly Durations',
          options: [
            {
              title: 'Last 30 Days',
              value: 'last-30'
            },
            {
              value: 'this-month',
              title: 'Current Month'
            },
            {
              value: 'last-month',
              title: 'Last Month'
            }
          ]
        },
        {
          label: 'Custom',
          options: [
            {
              value: 'custom',
              title: 'Custom Range'
            }
          ]
        }
      ]
    };
  },
  watch: {
    timingFilter: {
      handler: function () {
        if (this.timingFilter != 'custom') {
          this.fetchSummaryData();
        }
      }
    }
  },
  methods: {
    // Calculate date range based on timing filter
    calculateDateRange() {
      let start = null;
      let end = null;

      if (this.timingFilter === 'custom') {
        if (this.customTimeRange && this.customTimeRange.length === 2) {
          start = moment(this.customTimeRange[0]).startOf('day');
          end = moment(this.customTimeRange[1]).endOf('day');

          // Validate maximum 90 days for custom range
          const diff = end.diff(start, 'days');
          if (diff > 90) {
            this.warningToast('Maximum 3 months are allowed for analysis.');
            return null;
          }
        } else {
          return null;
        }
      } else if (this.timingFilter === 'this-week') {
        start = moment().startOf('week');
        end = moment().endOf('day');
      } else if (this.timingFilter === 'last-week') {
        start = moment().subtract(1, 'week').startOf('week');
        end = moment().subtract(1, 'week').endOf('week');
      } else if (this.timingFilter === 'this-month') {
        start = moment().startOf('month');
        end = moment().endOf('day');
      } else if (this.timingFilter === 'last-month') {
        start = moment().subtract(1, 'month').startOf('month');
        end = moment().subtract(1, 'month').endOf('month');
      } else if (this.timingFilter === 'last-7') {
        start = moment().subtract(7, 'days').startOf('day');
        end = moment().endOf('day');
      } else if (this.timingFilter === 'last-30') {
        start = moment().subtract(30, 'days').startOf('day');
        end = moment().endOf('day');
      }

      if (start && end) {
        this.defaultDuration.start = start.toISOString();
        this.defaultDuration.end = end.toISOString();
        this.dateRange = [start.toDate(), end.toDate()];

        // Set display time range
        this.displayTimeRange = {
          start: start.format('DD-MMM-YYYY'),
          end: end.format('DD-MMM-YYYY')
        };

        return true;
      }
      return false;
    },

    async fetchSummaryData() {
      // Calculate date range based on timing filter
      if (!this.calculateDateRange()) {
        this.fetchingData = false;
        return;
      }

      this.fetchingData = true;
      try {
        const params = {
          duration: this.defaultDuration,
          eventName: null
        };

        const response = await eventTrackingErrorsModule.getErrorsSummary(params);
        this.summaryData = response.data;

        // Prepare chart data
        this.prepareChartData();
      } catch (error) {
        console.error('Error fetching summary data:', error);
        this.errorToast('Failed to fetch event tracking errors summary.');
      } finally {
        this.fetchingData = false;
      }
    },

    prepareChartData() {
      if (!this.summaryData || !this.summaryData.daywiseCountArr) {
        this.chartOptions = null;
        return;
      }

      // Sort by date ascending for chart
      const sortedData = [...this.summaryData.daywiseCountArr].sort((a, b) => {
        return moment(a.date).valueOf() - moment(b.date).valueOf();
      });

      // Extract categories and data
      const categories = sortedData.map((item) => moment(item.date).format('DD MMM'));
      const data = sortedData.map((item) => parseInt(item.count));

      // Create Highcharts options
      this.chartOptions = {
        chart: {
          type: 'column',
          height: 300
        },
        title: {
          text: null
        },
        legend: {
          enabled: false
        },
        xAxis: {
          categories: categories,
          title: {
            text: 'Date'
          }
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Error Count'
          }
        },
        tooltip: {
          headerFormat: '<b>{point.x}</b><br/>',
          pointFormat: 'Errors: {point.y}'
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: false
            },
            enableMouseTracking: true,
            borderWidth: 0
          }
        },
        series: [
          {
            name: 'Errors',
            data: data,
            color: '#0080FF'
          }
        ]
      };
    },

    onDateRangeChange() {
      if (this.timingFilter === 'custom' && this.customTimeRange && this.customTimeRange.length === 2) {
        this.fetchSummaryData();
      }
    },

    setLast7Days() {
      this.timingFilter = 'last-7';
      this.calculateDateRange();
    },

    onEventRowClick(row) {
      // Navigate to detail page for this event
      this.$router.push({
        name: 'EventTrackingErrorsDetail',
        params: { eventName: row.event },
        query: {
          start: this.defaultDuration.start,
          end: this.defaultDuration.end
        }
      });
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '--';
      return moment(dateTime).format('DD-MMM-YYYY HH:mm');
    },

    getFormattedDateRange() {
      if (this.defaultDuration.start && this.defaultDuration.end) {
        const start = moment(this.defaultDuration.start).format('DD MMM');
        const end = moment(this.defaultDuration.end).format('DD MMM');
        return `${start} - ${end}`;
      }
      return '--';
    },

    getNumberWithComma(num) {
      if (num === null || num === undefined) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  },

  beforeDestroy() {
    document.getElementById('subSideMenu').style.display = 'block';
  },

  mounted() {
    document.getElementById('subSideMenu').style.display = 'none';
  },

  created() {
    // Set default to last 7 days - ensure timingFilter is set
    this.timingFilter = 'last-7';
    this.calculateDateRange();
    this.fetchSummaryData();
  }
};
